import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EmailListTab from './email-list-tab';
import { EmailListTabProps, ProspectEmailListArgs } from './utils/helper';
import {
  getFieldsRequest,
  getMinimalEmailAccountListRequest,
} from '../../../../../../extra-actions';
import { RootState } from '../../../../../../../../store/root-reducer';
import {
  deleteProspectEmailRequest,
  getProspectEmailsTabListingRequest,
  replyDraftDiscardRequest,
  replyDraftRequest,
  replyEmailRequest,
  sendEmailProspectTabRequest,
  getEmailThreadActivityRequest,
} from '../../../../../../../unified-inbox/extra-actions';
import {
  ReplyDraftDiscardPayload,
  ReplyDraftPayload,
  ReplyEmailPayload,
  SendEmailProspectTabProps,
} from '../../../../../../../unified-inbox/type';
import {
  resetDeleteProspectEmailRequest,
  resetEmailDraftData,
  resetGetProspectEmailsTabListingRequest,
  resetReplyDraftDiscardRequest,
  resetReplyForwardDraftRequest,
  resetSendEmailProspectTabRequest,
  setSelectedReplyEmailId,
  resetUnifiedInboxEmailThreadActivity,
} from '../../../../../../../unified-inbox/unified-inbox-slice';
import {
  AttachmentDeletePayload,
  AttachmentUploadPayload,
} from '../../../../../../../sequence/types';
import {
  deleteAttachmentRequest,
  imageUploadRequest,
  uploadAttachmentRequest,
} from '../../../../../../../sequence/extra-actions';
import {
  resetDeleteAttachmentRequest,
  resetUploadAttachmentRequest,
} from '../../../../../../../sequence/sequence-slice';
import { getMinimalEmailAccountListProps } from '../../../../../../types';
import { setProspectActivityCount } from '../../../../../../prospect-slice';

const EmailListTabContainer = memo((props: EmailListTabProps) => {
  const dispatch = useDispatch();

  const fetchFields = () => {
    dispatch(getFieldsRequest({}));
  };
  const fetchEmailListData = (payload: ProspectEmailListArgs) => {
    dispatch(getProspectEmailsTabListingRequest(payload));
  };
  const sendEmailProspectTab = (payload: SendEmailProspectTabProps) => {
    dispatch(sendEmailProspectTabRequest(payload));
  };
  const deleteProspectEmailRequests = (
    emailId: number,
    shouldThreadDelete?: boolean,
  ) => {
    dispatch(deleteProspectEmailRequest({ emailId, shouldThreadDelete }));
  };
  const draftProspectEmailRequest = (payload: ReplyDraftPayload) => {
    dispatch(replyDraftRequest(payload));
  };
  // Reset Fetch Email Tab Listing
  const resetGetProspectEmailsTabListingRequests = () => {
    dispatch(resetGetProspectEmailsTabListingRequest());
  };
  // Reset Delete Single Email
  const resetDeleteProspectEmailRequests = () => {
    dispatch(resetDeleteProspectEmailRequest());
  };

  const getEmailAccounts = (filter: getMinimalEmailAccountListProps) => {
    dispatch(getMinimalEmailAccountListRequest(filter));
  };

  const resetSendEmailProspectTabRequests = () => {
    dispatch(resetSendEmailProspectTabRequest());
  };
  const sendReplyDraftDiscardRequest = (payload: ReplyDraftDiscardPayload) => {
    dispatch(replyDraftDiscardRequest(payload));
  };

  const resetReplyForwardDraftRequests = (emailId: number) => {
    dispatch(resetReplyForwardDraftRequest(emailId));
  };

  const handleSendAttachmentUploadRequest = useCallback(
    (payload: AttachmentUploadPayload) => {
      dispatch(uploadAttachmentRequest(payload));
    },
    [dispatch],
  );

  const handleSendDeleteAttachmentRequest = useCallback(
    (payload: AttachmentDeletePayload) => {
      dispatch(deleteAttachmentRequest(payload));
    },
    [dispatch],
  );

  const handleResetUploadAttachmentRequest = useCallback(() => {
    dispatch(resetUploadAttachmentRequest());
  }, [dispatch]);

  const handleResetDeleteAttachmentRequest = useCallback(() => {
    dispatch(resetDeleteAttachmentRequest());
  }, [dispatch]);
  const handleImageUploadRequest = useCallback(
    (formData, onUploadProgress) =>
      Promise.resolve(
        dispatch(imageUploadRequest({ formData, onUploadProgress })),
      ) as Promise<any>,
    [dispatch],
  );

  const fields = useSelector((state: RootState) => state?.prospect?.fields);
  const emailListItems = useSelector(
    (state: RootState) => state?.unifiedInbox?.prospectEmailTabListingData,
  );
  // Note: totalItems, page, and pageLimit should be passed as props
  // or retrieved from API response metadata if available
  // For now, using defaults if not provided
  const pageLimit = props.pageLimit || 25;
  const currentPage = props.page || 1;
  const totalItems = props.totalItems || 0;
  const fetchEmailListDataStatus = useSelector(
    (state: RootState) =>
      state.unifiedInbox.getProspectEmailsTabListingRequest?.status,
  );
  const deleteProspectEmailStatus = useSelector(
    (state: RootState) =>
      state?.unifiedInbox?.deleteProspectEmailRequest?.status,
  );
  const sendEmailProspectTabStatus = useSelector(
    (state: RootState) => state?.unifiedInbox?.sendEmailProspectTabRequest,
  );
  const selectedRepliedEmailId = useSelector(
    (state: RootState) => state?.unifiedInbox?.selectedReplyEmailId,
  );

  const emailAccount = useSelector(
    (state: RootState) => state.prospect.getEmailAccountList,
  );
  const emailDraftData = useSelector(
    (state: RootState) => state.unifiedInbox.emailDraftData,
  );
  const resetEmailDraftDataRequest = () => {
    dispatch(resetEmailDraftData());
  };
  const singleProspectReplyEmailRequest = (payload: ReplyEmailPayload) => {
    dispatch(replyEmailRequest(payload));
  };
  const setSelectedRepliedEmailId = (emailId: number) => {
    dispatch(setSelectedReplyEmailId(emailId));
  };

  const resetReplyDraftDiscard = (emailId: number) => {
    dispatch(resetReplyDraftDiscardRequest(emailId));
  };

  const sendGetEmailThreadActivity = (payload) => {
    dispatch(getEmailThreadActivityRequest(payload));
  };
  const resetUnifiedInboxEmailThreadActivities = () =>
    dispatch(resetUnifiedInboxEmailThreadActivity());

  const editProspectAcitivityCount = (payload: any) => {
    dispatch(setProspectActivityCount(payload));
  };

  const resetEmailDraftDataRequestStatus = useSelector(
    (state: RootState) =>
      state.unifiedInbox.replyDraftDiscardRequest?.[
        Math.abs(Number(emailDraftData?.repliedOnEmailId)) ||
          Math.abs(Number(selectedRepliedEmailId))
      ]?.status,
  );

  const uploadAttachmentRequestStatus = useSelector(
    (state: RootState) => state.sequence.uploadAttachmentRequest.status,
  );
  const uploadAttachmentRequestError = useSelector(
    (state: RootState) => state.sequence.uploadAttachmentRequest.error,
  );
  const uploadedAttachment = useSelector(
    (state: RootState) => state.sequence.uploadedAttachment,
  );
  const deleteAttachmentRequestStatus = useSelector(
    (state: RootState) => state.sequence.deleteAttachmentRequest.status,
  );
  const deleteAttachmentRequestError = useSelector(
    (state: RootState) => state.sequence.deleteAttachmentRequest.error,
  );
  const deletedAttachmentId = useSelector(
    (state: RootState) => state.sequence.deletedAttachmentId,
  );
  const isDemoAccount = useSelector(
    (state: RootState) => state?.home?.isDemoAccount,
  );

  const replyEmailRequestStatus = useSelector((state: RootState) => {
    const emailId =
      Math.abs(Number(emailDraftData?.repliedOnEmailId)) ||
      Math.abs(Number(selectedRepliedEmailId));
    return state?.unifiedInbox?.replyEmailRequest[emailId];
  });

  const getEmailThreadActivityRequestStatus = useSelector(
    (state: RootState) =>
      state.unifiedInbox.getEmailThreadActivityRequest.status,
  );

  const unifiedInboxEmailThreadActivity = useSelector(
    (state: RootState) => state.unifiedInbox.unifiedInboxEmailThreadActivity,
  );

  // Get draft request status - using prospectId as key since we're composing new emails
  // For reply scenarios, we'd use emailId, but for new emails we use prospectId
  const draftProspectEmailRequestStatus = useSelector((state: RootState) => {
    // Since we're creating new drafts, we can use a generic key or the prospectId
    // The Redux state uses emailId as key, but for new drafts we might not have one
    // We'll use the first available draft status or undefined
    const draftRequests = state.unifiedInbox?.replyDraftRequest;
    if (!draftRequests) return undefined;
    // Get the most recent draft status (for new emails, we might need to track differently)
    const statuses = Object.values(draftRequests);
    return statuses.length > 0
      ? statuses[statuses.length - 1]?.status
      : undefined;
  });

  const emailRequestStatus = useSelector(
    (state: RootState) => state.unifiedInbox.sendEmailProspectTabRequest.status,
  );

  const prospectActivityCount = useSelector(
    (state: RootState) => state.prospect.prospectActivityCount,
  );

  return (
    // eslint-disable-next-line react/react-in-jsx-scope
    <EmailListTab
      {...props}
      fetchFields={fetchFields}
      fields={fields}
      fetchEmailListData={fetchEmailListData}
      emailListItems={emailListItems}
      sendEmailProspectTab={sendEmailProspectTab}
      deleteProspectEmailRequests={deleteProspectEmailRequests}
      fetchEmailListDataStatus={fetchEmailListDataStatus}
      resetGetProspectEmailsTabListingRequests={
        resetGetProspectEmailsTabListingRequests
      }
      resetSendEmailProspectTabRequests={resetSendEmailProspectTabRequests}
      resetDeleteProspectEmailRequests={resetDeleteProspectEmailRequests}
      getEmailAccounts={getEmailAccounts}
      deleteProspectEmailStatus={deleteProspectEmailStatus}
      draftProspectEmailRequest={draftProspectEmailRequest}
      sendReplyDraftDiscardRequest={sendReplyDraftDiscardRequest}
      resetReplyForwardDraftRequests={resetReplyForwardDraftRequests}
      draftProspectEmailRequestStatus={draftProspectEmailRequestStatus}
      emailDraftData={emailDraftData}
      emailAccount={emailAccount}
      sendEmailProspectTabStatus={sendEmailProspectTabStatus}
      page={currentPage}
      pageLimit={pageLimit}
      totalItems={totalItems}
      resetEmailDraftDataRequest={resetEmailDraftDataRequest}
      resetEmailDraftDataRequestStatus={resetEmailDraftDataRequestStatus}
      handleSendAttachmentUploadRequest={handleSendAttachmentUploadRequest}
      handleSendDeleteAttachmentRequest={handleSendDeleteAttachmentRequest}
      handleResetUploadAttachmentRequest={handleResetUploadAttachmentRequest}
      handleResetDeleteAttachmentRequest={handleResetDeleteAttachmentRequest}
      uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
      uploadAttachmentRequestError={uploadAttachmentRequestError}
      uploadedAttachment={uploadedAttachment}
      deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
      deleteAttachmentRequestError={deleteAttachmentRequestError}
      deletedAttachmentId={deletedAttachmentId}
      singleProspectReplyEmailRequest={singleProspectReplyEmailRequest}
      setSelectedRepliedEmailId={setSelectedRepliedEmailId}
      replyEmailRequestStatus={replyEmailRequestStatus}
      resetReplyDraftDiscard={resetReplyDraftDiscard}
      sendGetEmailThreadActivity={sendGetEmailThreadActivity}
      resetUnifiedInboxEmailThreadActivity={
        resetUnifiedInboxEmailThreadActivities
      }
      getEmailThreadActivityRequestStatus={getEmailThreadActivityRequestStatus}
      unifiedInboxEmailThreadActivity={unifiedInboxEmailThreadActivity}
      handleImageUploadRequest={handleImageUploadRequest}
      emailRequestStatus={emailRequestStatus}
      prospectActivityCount={prospectActivityCount}
      isDemoAccount={isDemoAccount}
      editProspectAcitivityCount={editProspectAcitivityCount}
    />
  );
});

export default EmailListTabContainer;
